import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "../../utils/axiosConfig";

import "../../styles/features/questionary/QuestParty.css";
import { useAuth0 } from "@auth0/auth0-react";

/** --- (opcjonalny) fallback: użyje się, jeśli API nie odpowie --- */
const FALLBACK_SCHEMA = []; // wkleić tu wersję offline

const KNOWN_OTHER_LABELS = [
  "Inne", "Inna", "Inny",
  "Inne (jakie?)", "Inna (jaka?)", "Inny (jaki?)",
  "Other", "Other (specify)"
];

function findOtherLabel(q) {
  if (q?.otherLabel) return q.otherLabel;
  const opts = Array.isArray(q?.options) ? q.options : [];
  return opts.find(o => KNOWN_OTHER_LABELS.includes(o)) || "Inne";
}

function extractOtherTextWithLabel(valueStr, label) {
  if (!valueStr || typeof valueStr !== "string") return "";
  const prefix = `${label}:`;
  return valueStr.startsWith(prefix) ? valueStr.slice(prefix.length).trimStart() : "";
}

/* proste debounce do autosave */
function useDebouncedCallback(fn, delay) {
  const ref = useRef({ t: null, fn });
  ref.current.fn = fn;
  return useCallback((...args) => {
    if (ref.current.t) clearTimeout(ref.current.t);
    ref.current.t = setTimeout(() => ref.current.fn(...args), delay);
  }, [delay]);
}

/** dopuszczamy 2 formaty: { sections: [...] } albo bezpośrednio [...] */
function normalizeSections(data) {
  if (data && Array.isArray(data.sections)) return data.sections;
  if (Array.isArray(data)) return data;
  return [];
}

function normalizeQuestionType(t) {
  const v = String(t || "").toLowerCase();
  if (v === "multi" || v === "checkbox" || v === "multiple") return "multi";
  if (v === "open" || v === "text" || v === "input") return "open";
  return "single";
}

export default function QuestParty() {
  const [schema, setSchema] = useState([]);
  const [answers, setAnswers] = useState({});
  const [expanded, setExpanded] = useState(() => new Set());
  const [active, setActive] = useState({ sectionId: null, questionId: null });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [schemaError, setSchemaError] = useState("");
  const [status, setStatus] = useState({ answered: 0, total: 0, complete: false, missing: [] });
  const [submitInfo, setSubmitInfo] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently } = useAuth0();

  // Ankieta tylko dla zalogowanych -> ochrona zaimplementowana w sciezkach
  // useEffect(() => {
  //   if (!isLoading && !isAuthenticated) {
  //     loginWithRedirect();
  //   }
  // }, [isLoading, isAuthenticated, loginWithRedirect]);

  const getAuthHeaders = useCallback(async () => {
    const audience = 'https://someet-api'; 
    const token = await getAccessTokenSilently({
      authorizationParams: audience ? { audience } : undefined,
    });
    return { Authorization: `Bearer ${token}` };
  }, [getAccessTokenSilently]);

  const apiGet = useCallback(async (url) => {
    const headers = await getAuthHeaders();
    return axios.get(url, { headers });
  }, [getAuthHeaders]);

  const apiPut = useCallback(async (url, body) => {
    const headers = await getAuthHeaders();
    return axios.put(url, body, { headers });
  }, [getAuthHeaders]);

  const apiPost = useCallback(async (url, body) => {
    const headers = await getAuthHeaders();
    return axios.post(url, body, { headers });
  }, [getAuthHeaders]);

  async function refreshStatus() {
    try {
      const { data } = await apiGet("/survey/status");
      setStatus(data);
    } catch {
      // ignoruj
    }
  }

  /* --- pobranie schematu i odpowiedzi użytkownika --- */
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    let mounted = true;
    (async () => {
      try {
        const [schemaRes, answersRes] = await Promise.allSettled([
          apiGet("/survey/schema"),
          apiGet("/survey/answers/me"),
        ]);

        if (!mounted) return;

        if (schemaRes.status !== "fulfilled") {
          setSchemaError("Nie udało się pobrać schematu ankiety – używam wersji offline.");
          setSchema(
            (FALLBACK_SCHEMA || []).map((sec) => ({
              ...sec,
              questions: (sec.questions ?? []).map((q) => ({
                ...q,
                type: normalizeQuestionType(q.type),
                options: Array.isArray(q.options) ? q.options : [],
              })),
            }))
          );
          return;
        }

        const sections = normalizeSections(schemaRes.value.data);
        const normalized = sections.map((sec) => ({
          ...sec,
          questions: (sec.questions ?? []).map((q) => ({
            ...q,
            type: normalizeQuestionType(q.type),
            options: Array.isArray(q.options) ? q.options : [],
          })),
        }));
        setSchema(normalized);

        if (!normalized.length) {
          setSchemaError("Schemat ankiety jest pusty lub w nieprawidłowym formacie.");
          return;
        }

        const a =
          answersRes.status === "fulfilled" && answersRes.value?.data
            ? answersRes.value.data
            : {};
        setAnswers(a);

        await refreshStatus();

        const firstSec = normalized[0];
        if (firstSec) {
          setExpanded(new Set([firstSec.id]));
          const firstQ = (firstSec.questions ?? [])[0];
          setActive({ sectionId: firstSec.id, questionId: firstQ?.id ?? null });
        }
      } catch {
        if (!mounted) return;
        setSchemaError("Wystąpił błąd podczas ładowania ankiety.");
      }
    })();

    return () => {
      mounted = false;
    };
  }, [ apiGet]);

  const allQuestions = useMemo(
    () =>
      schema.flatMap((sec) =>
        (sec.questions ?? []).map((q) => ({
          ...q,
          sectionId: sec.id,
        }))
      ),
    [schema]
  );

  const activeQuestion = useMemo(
    () => allQuestions.find((q) => q.id === active.questionId) || null,
    [allQuestions, active.questionId]
  );

  function isObject(val) {
    return val && typeof val === "object" && !Array.isArray(val);
  }

  function isAnswered(qid, a) {
    const v = a[qid];
    if (Array.isArray(v)) return v.length > 0;
    if (isObject(v)) {
      return Object.values(v).some((arr) => Array.isArray(arr) && arr.length > 0);
    }
    return v !== undefined && v !== null && String(v).trim() !== "";
  }

  const progress = useMemo(() => {
    const total = allQuestions.length;
    const answered = allQuestions.filter((q) => isAnswered(q.id, answers)).length;
    return { total, answered, pct: total ? Math.round((answered / total) * 100) : 0 };
  }, [allQuestions, answers]);

  function toggleSection(id) {
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  /* --- autosave --- */
  const debouncedSave = useDebouncedCallback(async (payload) => {
    try {
      setSaving(true);
      setError("");
      await apiPut("/survey/answers", payload);
      refreshStatus();
    } catch (e) {
      setError(e?.response?.data?.message || "Nie udało się zapisać odpowiedzi.");
    } finally {
      setSaving(false);
    }
  }, 600);

  function setAnswer(questionId, value, meta) {
    setAnswers((prev) => {
      const next = { ...prev, [questionId]: value };
      debouncedSave({ questionId, value, ...meta }); // jesli bedzie sypac błędami usunac meat bo backend ma restrykcyjne zasady
      return next;
    });
  }

  function go(step) {
    if (!activeQuestion) return;
    const idx = allQuestions.findIndex((q) => q.id === activeQuestion.id);
    const next = allQuestions[idx + step];
    if (next) setActive({ sectionId: next.sectionId, questionId: next.id });
  }

  function clearAnswer(qid) {
    setAnswers((p) => {
      const n = { ...p };
      delete n[qid];
      debouncedSave({ questionId: qid, value: null });
      return n;
    });
  }

  /* --- widget: Dni → Pory dnia --- */
  function DayTimeWidget({ q }) {
    const meta = q?.meta && typeof q.meta === "object" ? q.meta : {};
    const DAYS =
      Array.isArray(meta.days) && meta.days.length
        ? meta.days
        : Array.isArray(q.options) && q.options.length
          ? q.options
          : ["Pn–Czw", "Piątek", "Sobota", "Niedziela"];

    const SLOTS =
      Array.isArray(meta.slots) && meta.slots.length
        ? meta.slots
        : ["Rano", "Popołudnie", "Wieczór"];

    const current =
      answers[q.id] && typeof answers[q.id] === "object" && !Array.isArray(answers[q.id])
        ? answers[q.id]
        : {};

    const toggleDay = (day) => {
      const next = { ...current };
      if (Object.prototype.hasOwnProperty.call(next, day)) {
        delete next[day];
      } else {
        next[day] = [];
      }
      setAnswer(q.id, next, { sectionId: q.sectionId });
    };

    const toggleSlot = (day, slot) => {
      const dayArr = Array.isArray(current[day]) ? [...current[day]] : [];
      const i = dayArr.indexOf(slot);
      if (i >= 0) dayArr.splice(i, 1);
      else dayArr.push(slot);

      const next = { ...current, [day]: dayArr };
      if (!next[day].length) delete next[day];
      setAnswer(q.id, next, { sectionId: q.sectionId });
    };

    return (
      <div className="daytime">
        {DAYS.map((day) => {
          const dayChecked = Object.prototype.hasOwnProperty.call(current, day);
          const selectedSlots = Array.isArray(current[day]) ? current[day] : [];
          return (
            <div key={day} className="daytime-row">
              <label className="opt">
                <input type="checkbox" checked={dayChecked} onChange={() => toggleDay(day)} />
                <span>{day}</span>
              </label>

              {dayChecked && (
                <div className="daytime-slots">
                  {SLOTS.map((slot) => {
                    const checked = selectedSlots.includes(slot);
                    return (
                      <label key={slot} className="chip chk">
                        <input type="checkbox" checked={checked} onChange={() => toggleSlot(day, slot)} />
                        <span>{slot}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  /* --- render opcji --- */
  function renderOptions(q) {
    const widgetName = String(q?.widget || q?.meta?.widget || "")
      .toLowerCase()
      .replace(/_/g, "-")
      .replace(/\s+/g, "");

    if (widgetName === "day-time" || widgetName === "daytime") {
      return <DayTimeWidget q={q} />;
    }

    const name = `q_${q.id}`;
    const value = answers[q.id];
    const opts = Array.isArray(q.options) ? q.options : [];
    const OTHER_LABEL = findOtherLabel(q);

    if (q.type === "open") {
      return (
        <textarea
          className="open-input"
          value={typeof value === "string" ? value : ""}
          placeholder="Wpisz odpowiedź…"
          onChange={(e) => setAnswer(q.id, e.target.value, { sectionId: q.sectionId })}
        />
      );
    }

    if (q.type === "single") {
      const valStr = typeof value === "string" ? value : "";
      const isOtherSelected = valStr === OTHER_LABEL || valStr.startsWith(`${OTHER_LABEL}:`);
      const otherText = extractOtherTextWithLabel(valStr, OTHER_LABEL);

      return (
        <>
          {opts.map((opt, i) => {
            const id = `${name}_${i}`;
            const checked = valStr === opt || (isOtherSelected && opt === OTHER_LABEL);
            return (
              <label key={id} htmlFor={id} className="opt">
                <input
                  id={id}
                  type="radio"
                  name={name}
                  checked={checked}
                  onChange={() => {
                    if (opt === OTHER_LABEL) setAnswer(q.id, OTHER_LABEL, { sectionId: q.sectionId });
                    else setAnswer(q.id, opt, { sectionId: q.sectionId });
                  }}
                />
                <span>{opt}</span>
              </label>
            );
          })}

          {isOtherSelected && (
            <div className="other-wrap">
              <input
                type="text"
                className="other-input"
                placeholder="Doprecyzuj: jaka odpowiedź?"
                value={otherText}
                onChange={(e) => {
                  const txt = e.target.value || "";
                  const normalized = txt.trim() ? `${OTHER_LABEL}: ${txt}` : OTHER_LABEL;
                  setAnswer(q.id, normalized, { sectionId: q.sectionId });
                }}
              />
            </div>
          )}
        </>
      );
    }

    // multi
    const selected = Array.isArray(value) ? value : [];
    const hasOtherEntry = selected.some(v => v === OTHER_LABEL || v.startsWith(`${OTHER_LABEL}:`));
    const otherItemIndex = selected.findIndex(v => v === OTHER_LABEL || v.startsWith(`${OTHER_LABEL}:`));
    const otherTextMulti = otherItemIndex >= 0 ? extractOtherTextWithLabel(selected[otherItemIndex], OTHER_LABEL) : "";

    return (
      <>
        {opts.map((opt, i) => {
          const id = `${name}_${i}`;
          const checked = opt === OTHER_LABEL ? hasOtherEntry : selected.includes(opt);

          return (
            <label key={id} htmlFor={id} className="opt">
              <input
                id={id}
                type="checkbox"
                checked={checked}
                onChange={() => {
                  if (opt !== OTHER_LABEL) {
                    const next = checked ? selected.filter((x) => x !== opt) : [...selected, opt];
                    setAnswer(q.id, next, { sectionId: q.sectionId });
                    return;
                  }

                  if (hasOtherEntry) {
                    const next = selected.filter(
                      (x) => x !== OTHER_LABEL && !x.startsWith(`${OTHER_LABEL}:`)
                    );
                    setAnswer(q.id, next, { sectionId: q.sectionId });
                  } else {
                    const next = [...selected, OTHER_LABEL];
                    setAnswer(q.id, next, { sectionId: q.sectionId });
                  }
                }}
              />
              <span>{opt}</span>
            </label>
          );
        })}

        {hasOtherEntry && (
          <div className="other-wrap">
            <input
              type="text"
              className="other-input"
              placeholder="Doprecyzuj: jaka odpowiedź?"
              value={otherTextMulti}
              onChange={(e) => {
                const txt = e.target.value || "";
                const normalized = txt.trim() ? `${OTHER_LABEL}: ${txt}` : OTHER_LABEL;
                const base = selected.filter(
                  (x) => x !== OTHER_LABEL && !x.startsWith(`${OTHER_LABEL}:`)
                );
                setAnswer(q.id, [...base, normalized], { sectionId: q.sectionId });
              }}
            />
          </div>
        )}
      </>
    );
  }

  if (isLoading) {
    return <div style={{ padding: 24 }}>Ładowanie…</div>;
  }

      async function submitSurvey() {
      try {
        const { data } = await apiPost("/survey/submit");
        setSubmitInfo(data);
        setSuccessModal(data);
        await refreshStatus();
      } catch (e) {
        alert(e?.response?.data?.message || "Nie udało się wysłać ankiety.");
  }
}

  return (
    <section className="survey">
      <aside className="survey-aside">
        <div className="aside-head">
          <h2>Twoja ankieta</h2>
          <div className="progress">
            <div className="bar" style={{ width: `${progress.pct}%` }} />
          </div>
          <div className="progress-meta">
            {progress.answered}/{progress.total} • {progress.pct}%
          </div>
          {schemaError && <div className="schema-error">{schemaError}</div>}
        </div>

        <nav className="sectionparty">
          {schema.map((sec) => (
            <div key={sec.id} className={`section ${expanded.has(sec.id) ? "open" : ""}`}>
              <button
                className="section-title-quest"
                onClick={() => {
                  toggleSection(sec.id);
                  const qs = sec.questions ?? [];
                  if (!qs.find((q) => q.id === active.questionId)) {
                    const first = qs[0];
                    if (first) setActive({ sectionId: sec.id, questionId: first.id });
                  }
                }}
                aria-expanded={expanded.has(sec.id)}
              >
                {sec.title}
                <span className="chev" aria-hidden>▾</span>
              </button>

              {expanded.has(sec.id) && (
                <ul className="question-list">
                  {(sec.questions ?? []).map((q) => {
                    const answered = isAnswered(q.id, answers);
                    const isActive = q.id === active.questionId;
                    return (
                      <li key={q.id}>
                        <button
                          className={`ql-item ${isActive ? "active" : ""}`}
                          onClick={() => setActive({ sectionId: sec.id, questionId: q.id })}
                        >
                          <span className={`dot ${answered ? "ok" : ""}`} aria-hidden />
                          <span className="ql-text">{q.title}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
        </nav>

        <div className="aside-submit">
          <button
            className="btn-quest btn-primary wide"
            disabled={!status.complete}
              onClick={async () => {
                try {
                  const { data } = await apiPost("/survey/submit");
                  setSubmitInfo(data);
                  setSuccessModal(data);
                  await refreshStatus();
                } catch (e) {
                  alert(e?.response?.data?.message || "Nie udało się wysłać ankiety.");
                }
              }}
          >
            Zatwierdź i wyślij
          </button>

          {submitInfo && <div className="aside-submit-info">Wysłano • wersja {submitInfo.version}</div>}
          {error && <div className="schema-error">{error}</div>}
        </div>
      </aside>

      <main className="survey-main">
        {activeQuestion ? (
        <div className="q-card">
          <div className="q-head">
            <h3>{activeQuestion.title}</h3>

            <div className="head-right">
              <div className="badges">
                <span className="badge">
                  {activeQuestion.widget
                    ? "Dni i pory dnia"
                    : activeQuestion.type === "single"
                      ? "Jednokrotny wybór"
                      : activeQuestion.type === "multi"
                        ? "Wielokrotny wybór"
                        : "Odpowiedź otwarta"}
                </span>
                {/* {saving && <span className="badge saving">Zapisywanie…</span>} */}
              </div>
            </div>
          </div>

          <div className="options">{renderOptions(activeQuestion)}</div>

            <div className="q-actions">
              <button className="btn-quest btn-ghost" onClick={() => clearAnswer(activeQuestion.id)}>
                Wyczyść
              </button>
              <div className="spacer" />
              <button className="btn-quest" onClick={() => go(-1)} disabled={allQuestions[0]?.id === activeQuestion.id}>
                ← Poprzednie
              </button>
                {allQuestions[allQuestions.length - 1]?.id === activeQuestion.id ? (
                  <button
                    className="btn-quest btn-primary"
                    onClick={submitSurvey}
                    disabled={!status.complete}
                  >
                    Wyślij ankietę
                  </button>
                ) : (
                  <button
                    className="btn-quest btn-primary"
                    onClick={() => go(1)}
                  >
                    Następne →
                  </button>
                )}
            </div>
          </div>
        ) : (
          <div className="q-empty">Wybierz pytanie z listy po lewej stronie.</div>
        )}
      </main>
      {successModal && (
        <div className="survey-modal-overlay" onClick={() => setSuccessModal(null)}>
          <div className="survey-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="survey-modal-close"
              onClick={() => setSuccessModal(null)}
              aria-label="Zamknij"
            >
              ×
            </button>

            <div className="survey-modal-icon">✓</div>

            <h2>Dziękujemy!</h2>

            <p>
              Twoje odpowiedzi zostały wysłane poprawnie.
              Możesz wrócić do ankiety i zmienić je w dowolnym momencie.
            </p>

            <button
              className="btn-quest btn-primary"
              onClick={() => setSuccessModal(null)}
            >
              Zamknij
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
